import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'                      // <<-- added
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faGraduationCap, 
  faUsers, 
  faChartLine, 
  faRocket,
  faArrowRight,
  faBrain,
  faCogs,
  faLightbulb,
  faPlayCircle,
  faCheckCircle,
  faGlobe,
  faShieldAlt,
  faClock,
  faBalanceScale,
  faUpload,
  faFileAlt,
  faSearch,
  faEye,
  faChartBar,
  faRobot,
  faFilePdf,
  faImage,
  faCog
} from '@fortawesome/free-solid-svg-icons'
import './Home.css'
import bitsLogo from "../assets/bits_logo.png";

const Home = () => {
  const navigate = useNavigate()
  const [currentFeature, setCurrentFeature] = useState(0)
  const [isScrolled, setIsScrolled] = useState(false)

  const handleGetStarted = async () => {
    try {
      // Call the backend through the vite proxy. Vite proxy should forward /qprs -> your FastAPI (port 8019)
      const response = await axios.get(`/qpra/login`, {
        responseType: 'json',
      });
    
      if (response.status === 200) {
        const encodedResponse = response.data; 
  
        if (!encodedResponse) {
          console.error("The encoded_request is missing or undefined!");
          return;
        }
  
        const form = document.createElement('form');
        form.method = 'POST';
        // IdP SSO endpoint — keep this as your IdP Redirect endpoint
        form.action = 'https://idp.bits-pilani.ac.in/idp/profile/SAML2/Redirect/SSO';
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.name = 'SAMLRequest';
        hiddenInput.value = encodedResponse;
        form.appendChild(hiddenInput);
        document.body.appendChild(form);
        form.submit();
      } else {
        console.error("Login failed with status:", response.status);
      }
    } catch (error) {
      console.error("Error during login:", error);
    }
  };
  
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // Enhanced scroll effects
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset
      setIsScrolled(scrollTop > 100)
      
      // Animate elements on scroll
      const elements = document.querySelectorAll('.animate-on-scroll')
      elements.forEach((element) => {
        const elementTop = element.offsetTop
        const elementHeight = element.offsetHeight
        const windowHeight = window.innerHeight
        
        if (scrollTop + windowHeight > elementTop + elementHeight / 3) {
          element.classList.add('in-view')
        }
      })
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const features = [
    {
      icon: faUpload,
      title: "Smart File Upload & Processing",
      description: "Upload PDF/DOCX question papers with automatic question partitioning and metadata extraction"
    },
    {
      icon: faRobot,
      title: "AI-Powered Analysis",
      description: "Comprehensive question analysis with Bloom's taxonomy, quality rubrics, and time estimation"
    },
    {
      icon: faChartLine,
      title: "Similarity Matrix Generation",
      description: "Advanced algorithms that analyze faculty expertise and question content for optimal matching"
    },
    {
      icon: faUsers,
      title: "Fair Assignment Algorithm",
      description: "PeerReview4All algorithm ensures equitable distribution while maximizing expertise alignment"
    },
    {
      icon: faEye,
      title: "Expert Review System",
      description: "Comprehensive review workflow with feedback collection and quality assessment tracking"
    }
  ]

  const benefits = [
    {
      icon: faClock,
      title: "Time Savings",
      description: "Reduce manual processing time by 90% with automated AI analysis and assignment",
      color: "#3B82F6"
    },
    {
      icon: faBalanceScale,
      title: "Fair Distribution",
      description: "Ensure equitable workload distribution across all faculty members",
      color: "#10B981"
    },
    {
      icon: faBrain,
      title: "AI-Powered Insights",
      description: "Get detailed analysis of question quality, complexity, and improvement suggestions",
      color: "#F59E0B"
    },
    {
      icon: faCogs,
      title: "Scalable Solution",
      description: "Handle any number of question papers and faculty with intelligent automation",
      color: "#8B5CF6"
    },
    {
      icon: faCheckCircle,
      title: "Quality Assurance",
      description: "Comprehensive review workflows ensure high-quality feedback and validation",
      color: "#EF4444"
    }
  ]

  const workflowSteps = [
    {
      number: "01",
      title: "Upload Question Papers",
      description: "Upload PDF/DOCX files with automatic question extraction and metadata analysis",
      icon: faUpload
    },
    {
      number: "02",
      title: "AI Analysis",
      description: "Get comprehensive analysis of each question with quality scores and recommendations",
      icon: faRobot
    },
    {
      number: "03",
      title: "Generate Similarity Matrix",
      description: "AI analyzes expertise alignment and creates optimal matching scores",
      icon: faChartLine
    },
    {
      number: "04",
      title: "Fair Assignment",
      description: "PeerReview4All optimizes assignments for fairness and quality",
      icon: faUsers
    },
    {
      number: "05",
      title: "Expert Review",
      description: "Reviewers provide feedback and validate AI analysis with comprehensive review tools",
      icon: faEye
    }
  ]

  const capabilities = [
    {
      icon: faFilePdf,
      title: "Multi-Format Support",
      description: "Upload PDF and DOCX question papers with automatic processing"
    },
    {
      icon: faImage,
      title: "Vision AI Analysis",
      description: "Analyze questions with diagrams, images, and complex visual content"
    },
    {
      icon: faSearch,
      title: "Smart Metadata Extraction",
      description: "Automatic extraction of course names, marks, time, and question details"
    },
    {
      icon: faChartBar,
      title: "Quality Assessment",
      description: "Comprehensive rubrics for clarity, complexity, relevance, and engagement"
    },
    {
      icon: faEye,
      title: "Question Viewer",
      description: "View individual questions with preserved formatting and images"
    },
    {
      icon: faCog,
      title: "Advanced Caching",
      description: "Performance optimization with intelligent result caching"
    },
    {
      icon: faCheckCircle,
      title: "Review Workflow",
      description: "Complete review system with feedback collection and quality validation"
    }
  ]

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length)
    }, 4000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="landing-container">
      {/* Hero Section */}
      <header className="hero">
        <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
          <div className="container">
            <div className="logo">
              <img src={bitsLogo} alt="BITS Logo" className="bits-logo-img" />
              <span className="logo-subtitle">Question Review System</span>
            </div>
            <div className="nav-links">
              <button className="nav-button" onClick={() => scrollToSection('features')}>Features</button>
              <button className="nav-button" onClick={() => scrollToSection('capabilities')}>Capabilities</button>
              <button className="nav-button" onClick={() => scrollToSection('workflow')}>Workflow</button>
              <button className="nav-button" onClick={() => scrollToSection('benefits')}>Benefits</button>
            </div>
            <button className="cta-button" onClick={handleGetStarted}>Get Started</button>
          </div>
        </nav>
        
        <div className="hero-content">
          <div className="hero-text">
            <div className="badge">
              <FontAwesomeIcon icon={faRocket} />
              <span>Powered by </span>
              <div className="spanda-logo small">
                <span className="spanda-bracket">[</span>
                <span className="spanda-text">Spanda</span>
                <span className="spanda-dot">.</span>
                <span className="spanda-text">AI</span>
                <span className="spanda-bracket">]</span>
              </div>
              <span> Platform</span>
            </div>
            <h1>Complete AI-Powered Question Review System</h1>
            <p>
              Upload, analyze, assign, and review question papers with intelligent AI. From automatic question 
              extraction to fair faculty assignment and comprehensive review workflows, our system handles every aspect of 
              academic question paper management and evaluation.
            </p>
            
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">90%</div>
                <div className="stat-label">Time Saved</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">AI</div>
                <div className="stat-label">Powered Analysis</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">100%</div>
                <div className="stat-label">Fair Assignment</div>
              </div>
            </div>
            
          </div>
          
          <div className="hero-visual">
            <div className="feature-showcase">
              <div className="animated-feature-card">
                <div className="feature-icon-large">
                  <FontAwesomeIcon icon={features[currentFeature].icon} />
                </div>
                <h3>{features[currentFeature].title}</h3>
                <p>{features[currentFeature].description}</p>
              </div>
              <div className="feature-indicators">
                {features.map((_, index) => (
                  <div 
                    key={index} 
                    className={`indicator ${index === currentFeature ? 'active' : ''}`}
                    onClick={() => setCurrentFeature(index)}
                  />
                ))}
              </div>
            </div>
            
            <div className="hero-stats-cards">
              <div className="stats-card">
                <div className="stats-icon">📄</div>
                <div className="stats-content">
                  <div className="stats-number">PDF/DOCX</div>
                  <div className="stats-label">File Support</div>
                </div>
              </div>
              <div className="stats-card">
                <div className="stats-icon">🤖</div>
                <div className="stats-content">
                  <div className="stats-number">AI</div>
                  <div className="stats-label">Analysis</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <h2>Core Features</h2>
            <p>Complete workflow from upload to assignment with AI-powered intelligence</p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-box animate-on-scroll">
                <div className="icon-container">
                  <FontAwesomeIcon icon={feature.icon} className="icon" />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section id="capabilities" className="capabilities-section">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <h2>Advanced Capabilities</h2>
            <p>Powerful tools and technologies for comprehensive question paper management</p>
          </div>
          
          <div className="capabilities-grid">
            {capabilities.map((capability, index) => (
              <div key={index} className="capability-box animate-on-scroll">
                <div className="icon-container">
                  <FontAwesomeIcon icon={capability.icon} className="icon" />
                </div>
                <h3>{capability.title}</h3>
                <p>{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="benefits-section">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <h2>Why Choose Our System?</h2>
            <p>Transform your academic review process with intelligent automation</p>
          </div>
          
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card animate-on-scroll">
                <div className="benefit-icon" style={{ color: benefit.color }}>
                  <FontAwesomeIcon icon={benefit.icon} />
                </div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section id="workflow" className="workflow-section">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <h2>Simple 4-Step Workflow</h2>
            <p>From upload to assignment in minutes with AI-powered automation</p>
          </div>
          
          <div className="workflow-steps">
            {workflowSteps.map((step, index) => (
              <div key={index} className="step animate-on-scroll">
                <div className="step-number">
                  {step.number}
                </div>
                <div className="step-content">
                  <div className="step-icon">
                    <FontAwesomeIcon icon={step.icon} />
                  </div>
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
                {index < workflowSteps.length - 1 && (
                  <div className="step-connector" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="container">
          <div className="cta-content">
            <div className="cta-text">
              <h2>Ready to Transform Your Question Review Process?</h2>
              <p>Join leading academic institutions using our complete AI-powered question paper review system</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
